import { InfoOutlineIcon } from '@ikyucom/design-system-icons-react'
import { ElementButton } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import { ConversionTag } from '~/components/ConversionTag'
import { format } from '~/lib/date/date'
import { getReservationConfirmUrl } from '~/lib/url'
import { useEnv } from '~/stores/env'
import { css, sva } from '~/styled-system/css'
import { useCancelPolicyStatus } from '../store/cancelPolicy'
import { useReservationResult } from '../store/stateMachine/context'
import { useIsGuest, useIsMember, useIsRequestReservation } from '../store/stateMachine/state'

export function SuccessMessage() {
  const isRequest = useIsRequestReservation()
  return (
    <p
      className={css({
        textAlign: 'center',
        fontSize: '3xl',
        fontWeight: 'bold',
        // TODO variant でやるべき...
        color: 'white',
        md: {
          color: 'tertiary',
          fontSize: '3xl',
        },
      })}
    >
      {isRequest ? (
        <>
          リクエスト予約のお申し込みを
          <br />
          受付けました
        </>
      ) : (
        <>
          ありがとうございます
          <br />
          ご予約が完了いたしました
        </>
      )}
    </p>
  )
}

const detailStyles = sva({
  slots: ['container', 'name', 'content'],
  base: {
    container: {
      spaceY: '3',
      md: {
        spaceY: '2',
      },
    },
    name: {
      textAlign: 'center',
      fontSize: '2xl',
      fontWeight: 'bold',
    },
    content: {
      spaceY: '1',
      textAlign: 'center',
    },
  },
})

export function Detail() {
  const { result } = useDetail()
  if (result === undefined) {
    return null
  }
  const { restaurant, reservationId, visitDate, visitTime, visitorsCount } = result.reservation
  const classes = detailStyles()
  return (
    <div className={classes.container}>
      <p className={classes.name}>{restaurant.name}</p>
      <div className={classes.content}>
        <p>
          {format(visitDate, 'M月d日(E)')} {visitTime} {visitorsCount}名
        </p>
        <p>予約番号：{reservationId}</p>
      </div>
    </div>
  )
}

function useDetail() {
  const result = useReservationResult()
  const isGuest = useIsGuest()
  return { result, isGuest }
}

const requestReservationStyles = sva({
  slots: ['container', 'title', 'content', 'item', 'status'],
  base: {
    container: {
      spaceY: '3',
      borderTop: 'medium',
      paddingTop: '5',
      paddingBottom: '2',
      textAlign: 'left',
      md: {
        display: 'inline-block',
        p: '3',
        border: '[1px solid]',
        borderColor: 'error.border',
        textAlign: 'center',
      },
    },
    title: {
      fontSize: 'xl',
      fontWeight: 'bold',
      color: 'error.text',
      textAlign: 'center',
      md: {
        fontSize: 'md',
      },
    },
    content: {
      spaceY: '1',
      bgColor: 'gray.100',
      p: '3',
      color: 'gray.700',
      fontSize: 'sm',
      md: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        spaceX: '5',
        spaceY: '0',
        p: '0',
        bgColor: '[none]',
      },
    },
    item: {
      md: {
        display: 'inline-block',
      },
    },
    status: {
      fontWeight: 'bold',
      color: 'gray.700',
    },
  },
})

export function RequestReservation() {
  const isRequest = useIsRequestReservation()
  if (!isRequest) {
    return null
  }
  const classes = requestReservationStyles()
  return (
    <div className={classes.container}>
      <p className={classes.title}>
        リクエスト予約のお申し込み時点では
        <br className={css({ md: { display: 'none' } })} />
        予約は完了していません
      </p>
      <p className={css({ fontSize: 'sm', textAlign: 'left' })}>
        予約成立・不成立については、予約詳細でご確認ください。
      </p>
      <div className={classes.content}>
        <p className={classes.item}>
          予約完了前：<span className={classes.status}>リクエスト中</span>
        </p>
        <p className={classes.item}>
          予約成立時：<span className={classes.status}>予約中</span>
        </p>
        <p className={classes.item}>
          予約不成立時：<span className={classes.status}>キャンセル</span>
        </p>
      </div>
    </div>
  )
}

export function ConfirmButton() {
  const isMember = useIsMember()
  return isMember ? <MemberConfirmButton /> : <GuestConfirmButton />
}

export function MemberConfirmButton() {
  const { onClick } = useMemberConfirmButton()
  return (
    <ElementButton
      type="submit"
      label="予約内容を確認する"
      size="large"
      color="secondary"
      onClick={onClick}
    />
  )
}

function useMemberConfirmButton() {
  const env = useEnv()
  const isRequest = useIsRequestReservation()
  const status = isRequest ? 'all' : undefined
  const onClick = useCallback(() => {
    window.location.href = getReservationConfirmUrl(env, status)
  }, [env, status])
  return { onClick }
}

function GuestConfirmButton() {
  const reservationId = useReservationResult()?.reservation.reservationId
  if (reservationId === undefined) {
    return null
  }
  return (
    <form method="post" action="/rsMirror/rsMirrorYCnfDtl.asp">
      <input type="hidden" name="reserveNo" value={reservationId} />
      <ElementButton type="submit" label="予約内容を確認する" size="large" color="secondary" />
    </form>
  )
}

const emailSendNotificationStyles = sva({
  slots: ['container', 'text', 'note'],
  base: {
    container: {
      borderTop: 'medium',
      paddingTop: '5',
      textAlign: 'left',
      spaceY: '1',
    },
    text: {
      fontSize: 'sm',
      color: 'gray.600',
    },
    note: {
      color: 'tertiary',
      fontSize: 'sm',
      md: {
        textAlign: 'left',
      },
    },
  },
})

export function EmailSendNotification() {
  const { isGuest } = useDetail()
  const isRequest = useIsRequestReservation()
  const subject = isRequest ? 'リクエスト予約申し込み受付' : '予約完了'
  const classes = emailSendNotificationStyles()
  return (
    <div className={classes.container}>
      {isGuest && (
        <p className={classes.note}>
          予約番号は、ご予約の確認や問い合わせの際に必要になります。メモを取るなど大切に保管してください。
        </p>
      )}
      <p className={classes.text}>
        ご登録のメールアドレス宛に、{subject}
        メールを送信いたしましたのでご確認ください。
        {/* リクエスト予約中は予約完了メールの再送ができない仕様 */}
        {!isRequest && (
          <>
            メールが届かない場合は、「予約内容を確認する」ボタンよりメールアドレスを確認の上、再受信をお手続きください。
          </>
        )}
      </p>
    </div>
  )
}

export function CancelPolicy() {
  const status = useCancelPolicyStatus()
  const className = css({
    display: 'flex',
    spaceX: '1',
    color: 'white',
    fontSize: 'sm',
    md: { color: 'gray.800' },
  })
  if (status.type === 'uncancellable') {
    return (
      <div className={className}>
        <InfoOutlineIcon className={css({ mt: '1', flexShrink: '0' })} />
        <p>
          当サイト上での予約変更・予約取消の操作期限を過ぎております。変更・取消については直接レストランへご連絡下さい。
        </p>
      </div>
    )
  }
  return (
    <div className={className}>
      <InfoOutlineIcon className={css({ mt: '1', flexShrink: '0' })} />
      <p>当サイトの予約変更、予約取消は{status.limitDay}中まで可能です。</p>
    </div>
  )
}

export function Conversion() {
  const tag = useReservationResult()?.affiliateSiteConversionTag
  if (tag === undefined) {
    return null
  }
  return <ConversionTag tag={tag} />
}
